import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { IconButton, Tooltip } from "@mui/material";
import { useMemo, useState } from "react";
import { useClientPermissionsContext } from "@entrilia/shared/contexts/ClientPermissionsContext.ts";
import useClientContext from "../../../../../../context/useClientContext.ts";
import useUserContext from "../../../../../../context/useUserContext.ts";
import type { ReportCompany } from "../../../sv/components/viewer/common/sharing/ShareReport.types.ts";
import ShareReportGroup from "../../../sv/components/viewer/common/sharing/ShareReportGroup.tsx";
import { useGroupsContext } from "../../groups/contexts/GroupsContext.ts";
import SharedWithIcons from "./SharedWithIcons.tsx";
interface Props {
  caption: string;
}

export default function GroupSharingCell({ caption }: Props) {
  const { clientCode } = useClientContext();
  const { groups, actions } = useGroupsContext();
  const { id: currentUserId, clients } = useUserContext();
  const { hasAnyPermissionForClient } = useClientPermissionsContext();
  const [open, setOpen] = useState(false);

  const group = useMemo(
    () => (groups[clientCode] ?? []).find((g) => g.caption === caption),
    [groups, clientCode, caption]
  );

  const company = useMemo((): ReportCompany | undefined => {
    const client = clients.find((c) => c.clientCode === clientCode);
    return client
      ? { clientCode: client.clientCode, name: client.title, logoMarkUrl: client.branding.logoMarkUrl }
      : undefined;
  }, [clients, clientCode]);

  const tooltip = useMemo(() => {
    const names = (group?.sharedWith ?? []).map((e) => e.name);
    return `Shared with\n${names.join("\n")}`;
  }, [group?.sharedWith]);

  // Group sharing affects every inheriting report, so it is limited to report admins (ManageAllReports).
  const canShare = hasAnyPermissionForClient(clientCode, ["ManageAllReports"]);
  const canOpen = canShare && !!company && !!actions.getSharing && !!actions.updateSharing;

  if (!group) {
    return null;
  }

  const sharedWith = group.sharedWith ?? [];

  const dialog =
    open && canOpen && company && actions.getSharing && actions.updateSharing ? (
      <ShareReportGroup
        clientCode={clientCode}
        groupId={group.id}
        groupCaption={group.caption}
        company={company}
        currentUserId={currentUserId}
        getSharing={actions.getSharing}
        updateSharing={actions.updateSharing}
        onClose={() => setOpen(false)}
      />
    ) : null;

  if (sharedWith.length > 0) {
    return (
      <>
        <SharedWithIcons
          sharedWith={sharedWith}
          tooltip={tooltip}
          onClick={canOpen ? () => setOpen(true) : undefined}
        />
        {dialog}
      </>
    );
  }

  if (!canOpen) {
    return null;
  }

  return (
    <>
      <Tooltip title="Share group" enterDelay={500} enterNextDelay={500} arrow disableInteractive>
        <IconButton className="sharing-action" onClick={() => setOpen(true)} sx={{ cursor: "pointer" }}>
          <ShareOutlinedIcon />
        </IconButton>
      </Tooltip>
      {dialog}
    </>
  );
}
